const CORS_ERROR_PATTERNS = [
  'failed to fetch',
  'load failed',
  'networkerror when attempting to fetch resource',
  'network request failed',
]

const PROBE_TIMEOUT_MS = 5000

// Model URLs are pre-signed (S3/CloudFront); the query string carries the
// signature and expiry, so it never reaches Sentry.
function sanitizeUrl(url) {
  if (typeof url !== 'string') {
    return null
  }

  return url.split('?')[0].split('#')[0]
}

// The host is the actionable part of a CORS failure (which bucket/CDN is
// missing the header), so it is surfaced as its own tag.
function hostOf(url) {
  try {
    return new URL(url, window.location.origin).host || null
  } catch {
    return null
  }
}

function toErrorInfo(error) {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack ?? null }
  }

  // three.js loaders reject with a ProgressEvent when the transport (not the
  // parser) fails, which stringifies to a useless "[object ProgressEvent]".
  if (error && typeof error === 'object') {
    return {
      name: error.constructor?.name ?? 'UnknownError',
      message: String(error.message ?? ''),
      stack: null,
    }
  }

  return { name: 'UnknownError', message: String(error), stack: null }
}

/**
 * Classifies why `viewer.loadModel` rejected.
 *
 * A blocked CORS response is opaque to JS: the browser strips the real reason
 * and the fetch rejects with a bare TypeError ("Failed to fetch" in Chrome,
 * "Load failed" in Safari) or, on the XHR path, a ProgressEvent with no
 * status. Both collapse to `cors_or_network` here - indistinguishable from a
 * genuine outage without the reachability probe below. An HttpError carries
 * `response`, so a readable 4xx/5xx is separable up front.
 */
export function classifyModelLoadError(error) {
  const status = error?.response?.status
  if (status) {
    return `http_${status}`
  }

  const { name, message } = toErrorInfo(error)
  const normalized = message.toLowerCase()

  if (name === 'TypeError' && CORS_ERROR_PATTERNS.some(pattern => normalized.includes(pattern))) {
    return 'cors_or_network'
  }

  if (typeof ProgressEvent !== 'undefined' && error instanceof ProgressEvent) {
    return 'cors_or_network'
  }

  return 'load_error'
}

/**
 * Re-requests the asset with `mode: 'no-cors'`, which skips the CORS check
 * entirely. The response is opaque (no status, no headers) but the promise
 * still settles on transport outcome, so it answers the one question the
 * original rejection cannot: did the request reach the server at all?
 *
 * Returns true (reached), false (did not reach), or null (inconclusive - the
 * probe timed out, or fetch is unavailable).
 *
 * Takes the signed URL, not the sanitized one: an unsigned URL would test a
 * 403 path instead of the real asset. It stays in the browser.
 */
async function isAssetReachable(url) {
  if (typeof fetch !== 'function' || !url) {
    return null
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)

  try {
    await fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store', signal: controller.signal })
    return true
  } catch {
    return controller.signal.aborted ? null : false
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Splits the ambiguous `cors_or_network` bucket using the reachability probe.
 *
 * `cors_blocked` means the server answered but the browser refused to hand the
 * response to JS - the ACAO mismatch this was written for, though an error
 * status served without CORS headers (e.g. an expired signature returning 403)
 * lands here too, since an opaque response exposes no status to tell them
 * apart. `network_unreachable` means the request never completed: offline,
 * DNS, or a blocking extension.
 */
async function resolveReason(reason, signedUrl) {
  if (reason !== 'cors_or_network') {
    return reason
  }

  const reachable = await isAssetReachable(signedUrl)
  if (reachable === true) {
    return 'cors_blocked'
  }
  if (reachable === false) {
    return 'network_unreachable'
  }

  return reason
}

/**
 * Reports a 3D model that failed to load in the /ai viewer.
 *
 * Reported as a purpose-built Error rather than by forwarding the raw
 * rejection: the global Sentry `ignoreErrors` list
 * (app/views/_fast_partials/_sentry_init.slim) drops bare "Load failed" /
 * "Failed to fetch" messages as third-party noise, and a CORS block surfaces
 * with exactly those messages - forwarding it would silently swallow the very
 * failures this reports. The original rejection is preserved in `extra`, and
 * not as `cause`, so the linked-error message cannot re-trigger that filter.
 *
 * Low-cardinality facets go in `tags` because Sentry does not index `extra`:
 * only tags can be searched and aggregated, and per-host breakdown is what
 * separates a CDN misconfiguration from an ordinary network flake.
 *
 * Never rejects - it runs inside a catch handler that must stay unaffected.
 */
export async function reportModelLoadFailure(captureException, error, context = {}) {
  try {
    const { url, format, providerModelId, isUploadedModel } = context
    const rawUrls = (Array.isArray(url) ? url : [url]).filter(Boolean)
    const urls = rawUrls.map(sanitizeUrl).filter(Boolean)

    const reason = await resolveReason(classifyModelLoadError(error), rawUrls[0])
    const { name, message, stack } = toErrorInfo(error)

    const reportedError = new Error(`3D Studio viewer failed to load model (${reason})`)
    reportedError.name = 'ModelLoadError'

    return captureException(reportedError, {
      tags: {
        model_load_reason: reason,
        model_load_source: isUploadedModel ? 'upload' : 'generation',
        model_host: (urls[0] && hostOf(urls[0])) || 'unknown',
        model_format: format || 'unknown',
      },
      extra: {
        modelUrls: urls,
        providerModelId: providerModelId ?? null,
        originalErrorName: name,
        originalErrorMessage: message,
        originalErrorStack: stack,
        httpStatus: error?.response?.status ?? null,
      },
    })
  } catch {
    return null
  }
}

export default reportModelLoadFailure
